import React, { useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { getYouTubeEmbedUrl } from '../../utils/youtube';

interface VideoLightboxProps {
  isOpen: boolean;
  onClose: () => void;
  videoId: string | null;
  videoUrl?: string | null;
}

const VideoLightbox: React.FC<VideoLightboxProps> = ({ isOpen, onClose, videoId, videoUrl }) => {
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when lightbox is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !videoId) return null;

  const embedUrl = getYouTubeEmbedUrl(videoId);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="video-lightbox-title"
    >
      <div
        className="relative w-full max-w-5xl bg-card rounded-lg overflow-hidden elevation-lg animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 bg-background/80 hover:bg-background"
          aria-label="Close video"
        >
          <Icon name="X" size={24} />
        </Button>

        {/* Video Container */}
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}> {/* 16:9 aspect ratio */}
          <iframe
            src={`${embedUrl}?autoplay=1&rel=0`}
            title="Video player"
            className="absolute top-0 left-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            id="video-lightbox-title"
          />
        </div>

        {/* Optional: Link to YouTube */}
        {videoUrl && (
          <div className="p-4 bg-muted/50 border-t border-border flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Watch on YouTube</p>
            <Button
              variant="outline"
              size="sm"
              iconName="ExternalLink"
              iconPosition="right"
              onClick={() => {
                window.open(videoUrl, '_blank', 'noopener,noreferrer');
              }}
            >
              Open in YouTube
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoLightbox;

